/**
 * JsonTreeView Component
 *
 * A collapsible tree view for JSON data with:
 * - Expand/collapse nested objects and arrays
 * - Type indicators (object, array, string, number, boolean, null)
 * - Copy value to clipboard
 * - Search highlighting
 * - Keyboard navigation
 */

import { useState, useCallback, useMemo, memo } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Tooltip,
  Text,
} from '@fluentui/react-components';
import {
  ChevronRightRegular,
  ChevronDownRegular,
  CopyRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    lineHeight: '1.5',
    overflow: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: `${tokens.spacingVerticalXXS} 0`,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    borderRadius: tokens.borderRadiusSmall,
  },
  rowHighlight: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
  },
  indent: {
    display: 'inline-block',
    width: '20px',
    flexShrink: 0,
  },
  expandButton: {
    minWidth: '20px',
    width: '20px',
    height: '20px',
    padding: 0,
    flexShrink: 0,
  },
  expandPlaceholder: {
    width: '20px',
    flexShrink: 0,
  },
  key: {
    color: tokens.colorPaletteBlueForeground2,
    marginRight: tokens.spacingHorizontalXS,
  },
  colon: {
    color: tokens.colorNeutralForeground3,
    marginRight: tokens.spacingHorizontalXS,
  },
  // Type-specific value colors
  valueString: {
    color: tokens.colorPaletteGreenForeground1,
    wordBreak: 'break-word',
  },
  valueNumber: {
    color: tokens.colorPaletteBlueForeground2,
  },
  valueBoolean: {
    color: tokens.colorPalettePurpleForeground2,
  },
  valueNull: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  // Brackets
  bracket: {
    color: tokens.colorNeutralForeground2,
  },
  bracketCollapsed: {
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXS,
  },
  // Type badges
  typeBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXS,
    fontStyle: 'italic',
  },
  // Copy button
  copyButton: {
    opacity: 0,
    marginLeft: 'auto',
    transitionProperty: 'opacity',
    transitionDuration: tokens.durationFaster,
  },
  copyButtonVisible: {
    opacity: 1,
  },
  // Empty state
  emptyState: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    padding: tokens.spacingVerticalS,
  },
});

interface JsonTreeNodeProps {
  keyName: string | undefined;
  value: unknown;
  depth: number;
  defaultExpandedDepth: number;
  searchQuery: string | undefined;
  path: string;
}

const JsonTreeNode = memo(({
  keyName,
  value,
  depth,
  defaultExpandedDepth,
  searchQuery,
  path,
}: JsonTreeNodeProps) => {
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = useState(depth < defaultExpandedDepth);
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const textToCopy = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [value]);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'ArrowRight' && !isExpanded) {
      setIsExpanded(true);
    } else if (e.key === 'ArrowLeft' && isExpanded) {
      setIsExpanded(false);
    }
  }, [handleToggle, isExpanded]);

  // Check if search matches this node
  const matchesSearch = useMemo(() => {
    if (!searchQuery) return false;
    const query = searchQuery.toLowerCase();
    if (keyName?.toLowerCase().includes(query)) return true;
    if (typeof value === 'string' && value.toLowerCase().includes(query)) return true;
    if (typeof value === 'number' && String(value).includes(query)) return true;
    return false;
  }, [searchQuery, keyName, value]);

  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);

  // Render primitive values
  const renderValue = () => {
    if (value === null) {
      return <span className={styles.valueNull}>null</span>;
    }
    if (typeof value === 'boolean') {
      return <span className={styles.valueBoolean}>{value ? 'true' : 'false'}</span>;
    }
    if (typeof value === 'number') {
      return <span className={styles.valueNumber}>{value}</span>;
    }
    if (typeof value === 'string') {
      const displayValue = value.length > 200 ? value.substring(0, 200) + '...' : value;
      return <span className={styles.valueString}>"{displayValue}"</span>;
    }
    return null;
  };

  // Render collapsed preview
  const renderCollapsedPreview = () => {
    if (isArray) {
      const arr = value as unknown[];
      return (
        <span className={styles.bracketCollapsed}>
          [{arr.length} items]
        </span>
      );
    }
    if (isObject) {
      const keys = Object.keys(value as object);
      return (
        <span className={styles.bracketCollapsed}>
          {'{'}...{keys.length} keys{'}'}
        </span>
      );
    }
    return null;
  };

  const indentation = Array(depth).fill(null).map((_, i) => (
    <span key={i} className={styles.indent} />
  ));

  // For primitive values
  if (!isObject) {
    return (
      <div
        className={`${styles.row} ${matchesSearch ? styles.rowHighlight : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {indentation}
        <span className={styles.expandPlaceholder} />
        {keyName !== undefined && (
          <>
            <span className={styles.key}>"{keyName}"</span>
            <span className={styles.colon}>:</span>
          </>
        )}
        {renderValue()}
        <Tooltip content={copied ? 'Copied!' : 'Copy value'} relationship="label">
          <Button
            className={`${styles.copyButton} ${isHovered ? styles.copyButtonVisible : ''}`}
            appearance="subtle"
            size="small"
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            onClick={handleCopy}
          />
        </Tooltip>
      </div>
    );
  }

  // For objects and arrays
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as object);

  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';

  return (
    <>
      <div
        className={`${styles.row} ${matchesSearch ? styles.rowHighlight : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {indentation}
        <Button
          className={styles.expandButton}
          appearance="subtle"
          size="small"
          icon={isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        />
        {keyName !== undefined && (
          <>
            <span className={styles.key}>"{keyName}"</span>
            <span className={styles.colon}>:</span>
          </>
        )}
        <span className={styles.bracket}>{openBracket}</span>
        {!isExpanded && renderCollapsedPreview()}
        {!isExpanded && <span className={styles.bracket}>{closeBracket}</span>}
        <Tooltip content={copied ? 'Copied!' : 'Copy value'} relationship="label">
          <Button
            className={`${styles.copyButton} ${isHovered ? styles.copyButtonVisible : ''}`}
            appearance="subtle"
            size="small"
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            onClick={handleCopy}
          />
        </Tooltip>
      </div>
      {isExpanded && (
        <>
          {entries.map(([k, v]) => (
            <JsonTreeNode
              key={k}
              keyName={isArray ? undefined : k}
              value={v}
              depth={depth + 1}
              defaultExpandedDepth={defaultExpandedDepth}
              searchQuery={searchQuery}
              path={`${path}.${k}`}
            />
          ))}
          <div className={styles.row}>
            {indentation}
            <span className={styles.expandPlaceholder} />
            <span className={styles.bracket}>{closeBracket}</span>
          </div>
        </>
      )}
    </>
  );
});

JsonTreeNode.displayName = 'JsonTreeNode';

export interface JsonTreeViewProps {
  /** The JSON data to display */
  data: unknown;
  /** Search query to highlight matches */
  searchQuery?: string;
  /** Depth to auto-expand (default: 2) */
  defaultExpandedDepth?: number;
  /** Maximum height of the container */
  maxHeight?: string;
}

export const JsonTreeView = memo(({
  data,
  searchQuery,
  defaultExpandedDepth = 2,
  maxHeight = '100%',
}: JsonTreeViewProps) => {
  const styles = useStyles();

  if (data === undefined || data === null) {
    return (
      <div className={styles.container}>
        <Text className={styles.emptyState}>No data</Text>
      </div>
    );
  }

  return (
    <div className={styles.container} style={{ maxHeight }}>
      <JsonTreeNode
        keyName={undefined}
        value={data}
        depth={0}
        defaultExpandedDepth={defaultExpandedDepth}
        searchQuery={searchQuery}
        path="$"
      />
    </div>
  );
});

JsonTreeView.displayName = 'JsonTreeView';
